import { ActionItem, Rock, Issue, Metric, User } from '../types';

/**
 * Utility functions for Action Item operations
 */

/**
 * Get the display name for a linked entity
 */
export function getLinkedEntityName(
  actionItem: ActionItem,
  rocks: Rock[],
  issues: Issue[],
  metrics: Metric[]
): string | null {
  if (actionItem.linkedRockId) {
    const rock = rocks.find((r) => r.id === actionItem.linkedRockId);
    return rock ? rock.rock : null;
  }
  if (actionItem.linkedIssueId) {
    const issue = issues.find((i) => i.id === actionItem.linkedIssueId);
    return issue ? issue.name : null;
  }
  if (actionItem.linkedMetricId) {
    const metric = metrics.find((m) => m.id === actionItem.linkedMetricId);
    return metric ? (metric.metricName || 'Unknown Metric') : null;
  }
  return null;
}

/**
 * Get the link type for an action item
 */
export function getActionItemLinkType(actionItem: ActionItem): 'rock' | 'issue' | 'metric' | 'standalone' {
  if (actionItem.linkedRockId || actionItem.rockId) return 'rock';
  if (actionItem.linkedIssueId) return 'issue';
  if (actionItem.linkedMetricId) return 'metric';
  return 'standalone';
}

/**
 * Check if an action item is overdue
 */
export function isActionItemOverdue(actionItem: ActionItem): boolean {
  if (actionItem.complete) return false;
  const dueDate = new Date(actionItem.dueDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);
  return dueDate < now;
}

/**
 * Get owner name for an action item
 */
export function getActionItemOwnerName(actionItem: ActionItem, users: User[]): string {
  if (!actionItem.ownerId) return 'Unassigned';
  const owner = users.find((u) => u.id === actionItem.ownerId);
  return owner?.name || 'Unknown';
}

/**
 * Get assignee name for an action item
 */
export function getActionItemAssigneeName(actionItem: ActionItem, users: User[]): string | null {
  if (!actionItem.assignedToUserId) return null;
  const assignee = users.find((u) => u.id === actionItem.assignedToUserId);
  return assignee?.name || null;
}

/**
 * Format date for display
 */
export function formatActionItemDate(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
